import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { showToastNotification, handleFormatAmount } from "@/utility/helper";
import {
  Calculator,
  TrendingUp,
  Wallet,
  Save,
  FileText,
  Pencil,
  PlusCircle,
  User,
  Calendar,
} from "lucide-react";
import type { OnboardTabProps } from "./onboard.types";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import { ThrowError } from "@/api/callApi";
import CommonModal from "@/components/common/CommonModal";
import {
  DocumentPreview,
  type TDocumentInput,
} from "@/utility/documentPreview";
import { useFormContext } from "react-hook-form";
import type {
  IAllowanceRow,
  IDeductionRow,
  IOverviewCards,
  IOverviewUser,
  ISalaryBreakdownItem,
  ISalaryHead,
} from "@/interface/salaryHead.interface";
import SalaryBreakdownSummaryTable from "@/components/common/SalaryBreakdownSummaryTable";

type SalaryHeadWithAmount = ISalaryHead & {
  amount: number;
  branchSalaryHeadId: string;
  displayAmount?: string;
};

const isBasicSalaryHead = (head?: {
  id?: string;
  name?: string;
  isBase?: boolean;
  salaryHeadId?: string;
  branchSalaryHeadId?: string;
}) => {
  if (!head) return false;
  const name = head.name?.toLowerCase().trim() || "";

  // Exclude Net and Gross salary from being treated as Basic Salary
  if (name.includes("net") || name.includes("gross")) return false;

  if (name.includes("basic") || name === "basic salary" || name === "basic") return true;
  if (head.isBase) return true;

  const id = head.id?.toLowerCase().trim() || "";
  const salaryHeadId = head.salaryHeadId?.toLowerCase().trim() || "";
  const branchSalaryHeadId = head.branchSalaryHeadId?.toLowerCase().trim() || "";
  return (
    id.includes("basic") ||
    salaryHeadId.includes("basic") ||
    branchSalaryHeadId.includes("basic")
  );
};

const OnboardSalaryDetail: React.FC<OnboardTabProps> = ({
  empId,
  isSalaryCreated: propIsSalaryCreated,
  handleNextStep,
}) => {
  const { watch } = useFormContext();
  const branchId = watch("branchId");

  // Form state
  const [salaryHeads, setSalaryHeads] = useState<SalaryHeadWithAmount[]>([]);
  const [headErrors, setHeadErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noHeads, setNoHeads] = useState(false);

  // PDF
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<TDocumentInput>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Salary
  const [salaryCreated, setSalaryCreated] =
    useState<boolean>(!!propIsSalaryCreated);
  const [showForm, setShowForm] = useState(false);

  // Overview
  const [overviewUser, setOverviewUser] = useState<IOverviewUser | null>(null);
  const [overviewMonth, setOverviewMonth] = useState<string>("");
  const [overviewCards, setOverviewCards] = useState<IOverviewCards | null>(
    null,
  );
  const [overviewBasic, setOverviewBasic] = useState<string>("");
  const [allowancesRows, setAllowancesRows] = useState<IAllowanceRow[]>([]);
  const [deductionsRows, setDeductionsRows] = useState<IDeductionRow[]>([]);
  const [salaryBreakdown, setSalaryBreakdown] = useState<
    ISalaryBreakdownItem[]
  >([]);

  const EXCLUDED_EARNING_NAMES = ["Gross Salary", "Net Salary"];
  const EXCLUDED_DEDUCTION_NAMES = ["Net Salary"];

  useEffect(() => {
    setSalaryCreated(!!propIsSalaryCreated);
  }, [propIsSalaryCreated]);

  const fetchData = async (forceFetchOverview = false) => {
    if (!empId || !branchId) {
      setSalaryHeads([]);
      setNoHeads(false);
      setSalaryBreakdown([]);
      setAllowancesRows([]);
      setDeductionsRows([]);
      setOverviewUser(null);
      setOverviewCards(null);
      setOverviewBasic("");
      setOverviewMonth("");
      return;
    }

    setIsLoading(true);
    setNoHeads(false);
    setSalaryBreakdown([]);
    setAllowancesRows([]);
    setDeductionsRows([]);
    setOverviewUser(null);
    setOverviewCards(null);
    setOverviewBasic("");
    setOverviewMonth("");

    try {
      const [earningRes, deductionRes] = await Promise.all([
        EmployeeOnBoardService.getBranchSalaryHeads({
          type: "earning",
          branchId,
          pageSize: 1000,
        }),
        EmployeeOnBoardService.getBranchSalaryHeads({
          type: "deduction",
          branchId,
          pageSize: 1000,
        }),
      ]);

      const earningItems = earningRes?.data?.branchSalaryHead || [];
      const deductionItems = deductionRes?.data?.branchSalaryHead || [];
      const allHeads = [...earningItems, ...deductionItems]
        .filter((item) => item.salaryHead != null)
        .map((item) => ({
          ...item.salaryHead!,
          amount: 0,
          branchSalaryHeadId: item.id,
        })) as SalaryHeadWithAmount[];

      if (allHeads.length === 0) {
        setNoHeads(true);
        setSalaryHeads([]);
        return;
      }

      let existingAmounts: {
        id?: string;
        branchSalaryHeadId?: string;
        name: string;
        amount: number;
      }[] = [];

      if (salaryCreated || forceFetchOverview) {
        const overviewRes =
          await EmployeeOnBoardService.getUserSalaryOverview(empId);
        if (overviewRes?.status && overviewRes?.data) {
          const data = overviewRes.data;

          setOverviewUser(data.user || null);
          setOverviewMonth(data.month || "");
          setOverviewCards(data.cards || null);
          setOverviewBasic(data.salary?.basic || "");
          setSalaryBreakdown(data.salaryBreakdown || []);

          const rawAllowances = data.allowancesBreakdown?.rows || [];
          const mappedAllowances: IAllowanceRow[] = rawAllowances.map(
            (row) => ({
              allowance: row.allowance || "",
              isFixed: row.isFixed || "--",
              isConditionBased: row.isConditionBased || "--",
              rate: row.rate || "--",
              basedOn: row.basedOn || "--",
              amount: row.amount ?? 0,
            }),
          );
          setAllowancesRows(mappedAllowances);

          const rawDeductions = data.deductionsBreakdown?.rows || [];
          const mappedDeductions: IDeductionRow[] = rawDeductions.map(
            (row) => ({
              deduction: row.deduction || "",
              isFixed: row.isFixed || "--",
              rate: row.rate || "--",
              amount: row.amount ?? 0,
            }),
          );
          setDeductionsRows(mappedDeductions);

          const earnings = rawAllowances
            .filter(
              (row) =>
                typeof row.allowance === "string" && row.allowance.length > 0,
            )
            .map((row) => ({
              name: row.allowance!,
              amount: row.amount ?? 0,
            }));

          const deductions = rawDeductions
            .filter(
              (row) =>
                typeof row.deduction === "string" && row.deduction.length > 0,
            )
            .map((row) => ({
              name: row.deduction!,
              amount: row.amount ?? 0,
            }));

          existingAmounts = [...earnings, ...deductions];

          if (data.salary?.basic !== undefined && data.salary?.basic !== null) {
            const basicRaw = String(data.salary.basic);
            const basicValue = parseFloat(basicRaw.replace(/,/g, ""));
            if (!isNaN(basicValue)) {
              const basicHead = allHeads.find((h) => isBasicSalaryHead(h));
              if (basicHead) {
                existingAmounts.push({
                  id: basicHead.id,
                  branchSalaryHeadId: basicHead.branchSalaryHeadId,
                  name: basicHead.name,
                  amount: basicValue,
                });
              }
            }
          }
        }
      }

      const merged = allHeads.map((head) => {
        const existing = existingAmounts.find(
          (item) =>
            (item.id && (item.id === head.id || item.id === head.branchSalaryHeadId)) ||
            (item.branchSalaryHeadId && item.branchSalaryHeadId === head.branchSalaryHeadId) ||
            (item.name && item.name.toLowerCase().trim() === head.name.toLowerCase().trim()),
        );
        const amount = existing ? existing.amount : 0;
        return {
          ...head,
          amount,
          displayAmount: amount > 0 ? String(amount) : "",
        };
      });

      setSalaryHeads(merged);
    } catch (error) {
      console?.log("fatchhhhh", error);
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [branchId, empId, salaryCreated]);

  const handleAmountChange = (branchSalaryHeadId: string, value: string) => {
    // 1. Remove anything except digits and dot
    let clean = value.replace(/[^0-9.]/g, "");

    // 2. Allow only a single dot
    const dotIndex = clean.indexOf(".");
    if (dotIndex !== -1) {
      const beforeDot = clean.slice(0, dotIndex);
      const afterDot = clean.slice(dotIndex + 1).replace(/\./g, "");
      clean = beforeDot + "." + afterDot;
    }

    let errorMsg = "";
    const parts = clean.split(".");

    // 3. Limit to maximum 7 digits before decimal point & show error
    if (parts[0].length > 7) {
      errorMsg = "You cannot enter more than 7 numbers (Max ₹99,99,999)";
      parts[0] = parts[0].slice(0, 7);
      clean = parts.length > 1 ? parts[0] + "." + parts[1] : parts[0];
    }

    // 4. Limit to maximum 2 digits after decimal point
    if (parts.length > 1 && parts[1].length > 2) {
      clean = parts[0] + "." + parts[1].slice(0, 2);
    }

    setHeadErrors((prev) => ({
      ...prev,
      [branchSalaryHeadId]: errorMsg,
    }));

    const numValue =
      clean === "" || clean === "." ? 0 : parseFloat(clean) || 0;

    setSalaryHeads((prev) =>
      prev.map((head) =>
        head.branchSalaryHeadId === branchSalaryHeadId
          ? { ...head, amount: numValue, displayAmount: clean }
          : head,
      ),
    );
  };

  const earnings = salaryHeads.filter(
    (h) => h.type === "earning" && !EXCLUDED_EARNING_NAMES.includes(h.name),
  );
  const deductions = salaryHeads.filter(
    (h) => h.type === "deduction" && !EXCLUDED_DEDUCTION_NAMES.includes(h.name),
  );

  const totalEarnings = earnings.reduce((sum, h) => sum + h.amount, 0);
  const totalDeductions = deductions.reduce((sum, h) => sum + h.amount, 0);
  const netSalary = totalEarnings - totalDeductions;

  const handleSubmit = async () => {
    if (!empId) {
      showToastNotification("danger", "Employee ID is missing.");
      return;
    }

    if (!salaryHeads || salaryHeads.length === 0) {
      showToastNotification("warning", "No salary components found to configure.");
      return;
    }

    const hasActiveHeadError = Object.values(headErrors).some((err) => !!err);
    if (hasActiveHeadError) {
      showToastNotification(
        "warning",
        "Please fix salary head amount errors before saving.",
      );
      return;
    }

    // 1. Basic Salary validation: Must be entered and > 0 (matched in active earnings)
    const basicHead = earnings.find((h) => isBasicSalaryHead(h));

    if (basicHead) {
      if (!basicHead.amount || basicHead.amount <= 0) {
        showToastNotification(
          "warning",
          `Please enter a valid amount for ${basicHead.name} (must be greater than 0).`,
        );
        return;
      }
    } else {
      // If no head explicitly named basic, ensure at least one earning head is entered
      const hasEarning = earnings.some((h) => h.amount > 0);
      if (!hasEarning) {
        showToastNotification(
          "warning",
          "Please enter at least one earning component amount greater than 0.",
        );
        return;
      }
    }

    // 2. Non-negative amount validation
    const hasNegative = salaryHeads.some((h) => (h.amount ?? 0) < 0);
    if (hasNegative) {
      showToastNotification(
        "warning",
        "Salary head amounts cannot be negative.",
      );
      return;
    }

    // 3. Net salary validation
    if (netSalary < 0) {
      showToastNotification(
        "warning",
        "Net salary cannot be negative. Total deductions exceed total earnings.",
      );
      return;
    }

    const payload = salaryHeads.map((head) => ({
      branchSalaryHeadId: head.branchSalaryHeadId,
      amount: head.amount,
    }));

    setIsSubmitting(true);
    try {
      const response = await EmployeeOnBoardService.saveUserSalaryHeads(
        empId,
        payload,
      );
      if (response?.status) {
        showToastNotification(
          "success",
          response.message || "Salary details saved successfully",
        );
        setSalaryCreated(true);
        setShowForm(false);
        if (handleNextStep) {
          handleNextStep("salary_detail", "tab9", []);
        }
        await fetchData(true);
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateOfferLetter = async () => {
    if (!empId) {
      showToastNotification("danger", "Employee ID is missing.");
      return;
    }
    setIsGeneratingPdf(true);
    try {
      const arrayBuffer =
        await EmployeeOnBoardService.generateOfferLetter(empId);
      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const fileName = `Offer_Letter_${empId}.pdf`;
      setSelectedDoc({ url, name: fileName });
      setShowPreview(true);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setSelectedDoc(null);
  };

  const renderSalarySummary = (title: string) => (
    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 shadow-xs">
      <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4">
        {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-2xs">
          <span className="text-xs font-medium text-slate-500 block">
            Total Earnings
          </span>
          <span className="text-lg font-bold text-emerald-600">
            {handleFormatAmount(totalEarnings)}
          </span>
        </div>
        <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-2xs">
          <span className="text-xs font-medium text-slate-500 block">
            Total Deductions
          </span>
          <span className="text-lg font-bold text-rose-600">
            {handleFormatAmount(totalDeductions)}
          </span>
        </div>
        <div className="bg-white p-3.5 rounded-lg border border-primary/20 bg-primary/5 shadow-2xs">
          <span className="text-xs font-medium text-primary block">
            Net Salary
          </span>
          <span className="text-lg font-bold text-primary">
            {handleFormatAmount(netSalary)}
          </span>
        </div>
      </div>
    </div>
  );

  const renderDetailTable = (
    rows: IAllowanceRow[] | IDeductionRow[],
    type: "allowance" | "deduction",
  ) => {
    const isAllowance = type === "allowance";
    const headerColor = isAllowance ? "text-emerald-700" : "text-rose-700";
    const bgHeader = isAllowance ? "bg-emerald-50/80" : "bg-rose-50/80";

    return (
      <div className="overflow-hidden rounded-xl border border-slate-200 shadow-xs">
        <div
          className={`px-5 py-3 border-b border-slate-200 font-semibold text-sm uppercase tracking-wider ${bgHeader} ${headerColor}`}
        >
          {isAllowance ? "Allowances Breakdown" : "Deductions Breakdown"}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50 text-xs font-semibold text-slate-500">
                <th className="py-2.5 px-4">Component</th>
                <th className="py-2.5 px-4">Type</th>
                {isAllowance && <th className="py-2.5 px-4">Condition</th>}
                <th className="py-2.5 px-4">Rate</th>
                {isAllowance && <th className="py-2.5 px-4">Based On</th>}
                <th className="py-2.5 px-4 text-right">Monthly (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row, idx) => (
                <tr
                  key={idx}
                  className="hover:bg-slate-50/80 transition-colors"
                >
                  <td className="py-2.5 px-4 font-medium text-slate-800">
                    {isAllowance
                      ? (row as IAllowanceRow).allowance
                      : (row as IDeductionRow).deduction}
                  </td>
                  <td className="py-2.5 px-4 text-slate-600">
                    {row.isFixed === "true" || row.isFixed === "Fixed"
                      ? "Fixed"
                      : row.isFixed === "false" || row.isFixed === "Variable"
                        ? "Variable"
                        : row.isFixed}
                  </td>
                  {isAllowance && (
                    <td className="py-2.5 px-4 text-slate-600">
                      {(row as IAllowanceRow).isConditionBased}
                    </td>
                  )}
                  <td className="py-2.5 px-4 text-slate-600">{row.rate}</td>
                  {isAllowance && (
                    <td className="py-2.5 px-4 text-slate-600">
                      {(row as IAllowanceRow).basedOn}
                    </td>
                  )}
                  <td className="py-2.5 px-4 text-right font-semibold text-slate-800">
                    {handleFormatAmount(row.amount)}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={isAllowance ? 6 : 4}
                    className="py-4 text-center text-slate-400"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSalaryTable = (
    items: SalaryHeadWithAmount[],
    type: "earning" | "deduction",
  ) => {
    const isEarning = type === "earning";
    const icon = isEarning ? (
      <TrendingUp className="w-4 h-4" />
    ) : (
      <Wallet className="w-4 h-4" />
    );
    const headerColor = isEarning ? "text-emerald-700" : "text-rose-700";
    const bgHeader = isEarning ? "bg-emerald-50/80" : "bg-rose-50/80";

    if (items.length === 0) return null;

    return (
      <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm h-full flex flex-col">
        <div
          className={`px-5 py-3.5 border-b border-slate-200 flex items-center gap-2.5 ${bgHeader} ${headerColor}`}
        >
          {icon}
          <span className="font-semibold text-sm uppercase tracking-wider">
            {isEarning ? "Earnings" : "Deductions"}
          </span>
        </div>

        <div className="divide-y divide-slate-100 flex-1">
          {items.map((head, idx) => {
            const isBaseHead = isBasicSalaryHead(head);
            return (
              <div
                key={head.branchSalaryHeadId}
                className={`flex items-center px-5 py-3 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                  } hover:bg-slate-100/60`}
              >
                <div className="w-1/2 text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <span>{head.name}</span>
                  {isBaseHead && (
                    <span
                      className="text-red-500 font-bold text-xs"
                      title="Required"
                    >
                      *
                    </span>
                  )}
                </div>
                <div className="w-1/2 flex flex-col items-end">
                  <div className="relative w-full max-w-[180px]">
                    <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center text-slate-400 text-sm">
                      ₹
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={
                        head.displayAmount !== undefined
                          ? head.displayAmount
                          : head.amount > 0
                            ? String(head.amount)
                            : ""
                      }
                      onChange={(e) =>
                        handleAmountChange(
                          head.branchSalaryHeadId,
                          e.target.value,
                        )
                      }
                      className={`w-full rounded-md border ${headErrors[head.branchSalaryHeadId]
                        ? "border-red-500 ring-1 ring-red-500 focus:border-red-500 focus:ring-red-200"
                        : "border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                        } bg-white pl-7 pr-3 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none transition-shadow`}
                      placeholder="0.00"
                    />
                  </div>
                  {headErrors[head.branchSalaryHeadId] && (
                    <p className="text-[11px] text-red-500 font-medium mt-1 text-right">
                      {headErrors[head.branchSalaryHeadId]}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!branchId) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="p-3 bg-slate-100 rounded-full mb-4">
            <Calculator className="w-8 h-8 text-slate-400" />
          </div>
          <h4 className="text-lg font-semibold text-slate-700">
            No Branch Selected
          </h4>
          <p className="text-sm text-slate-500 max-w-sm mt-1">
            Please select a branch in the Permissions step first.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  if (noHeads) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="p-3 bg-amber-50 rounded-full mb-4">
            <Wallet className="w-8 h-8 text-amber-500" />
          </div>
          <h4 className="text-lg font-semibold text-slate-700">
            No Salary Heads Found
          </h4>
          <p className="text-sm text-slate-500 max-w-sm mt-1">
            No salary Heads are configured for this branch. Please contact HQ HR or Site Admin .
          </p>
        </div>
      </div>
    );
  }

  if (!salaryCreated && !showForm) {
    return (
      <div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="p-3 bg-primary/10 rounded-full mb-4">
            <PlusCircle className="w-8 h-8 text-primary" />
          </div>
          <h4 className="text-lg font-semibold text-slate-700">
            No Salary Created
          </h4>
          <p className="text-sm text-slate-500 max-w-sm mt-1 mb-6">
            Configure the salary structure for this employee.
          </p>
          <Button
            onClick={() => {
              setShowForm(true);
              fetchData(true);
            }}
            className="gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            Create Salary
          </Button>
        </div>
      </div>
    );
  }

  // Overview
  if (salaryCreated && !showForm) {
    const dailyWages = overviewCards?.dailyWages || 0;
    const grossSalary = overviewCards?.grossSalary || 0;
    const basicSalary = overviewBasic || 0;
    const totalAllowances = allowancesRows.reduce(
      (sum, r) => sum + r.amount,
      0,
    );
    const totalDeductionsOverview = deductionsRows.reduce(
      (sum, r) => sum + r.amount,
      0,
    );
    const netPay = grossSalary - totalDeductionsOverview;

    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-800">
              Salary Overview
            </h2>
            {overviewUser && (
              <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {overviewUser.name}
                </span>
                <span className="text-slate-300">|</span>
                <span className="font-mono">#{overviewUser.employeeCode}</span>
                {overviewMonth && (
                  <>
                    <span className="text-slate-300">|</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {overviewMonth}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(true);
                fetchData(true);
              }}
              className="gap-2"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              disabled={isGeneratingPdf}
              onClick={handleGenerateOfferLetter}
              className="gap-2"
            >
              {isGeneratingPdf ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              {isGeneratingPdf ? "Generating..." : "Offer Letter"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-500 font-medium">Daily Wages</p>
            <p className="text-lg font-bold text-slate-800">
              {handleFormatAmount(dailyWages)}
            </p>
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-500 font-medium">Gross Salary</p>
            <p className="text-lg font-bold text-slate-800">
              {handleFormatAmount(grossSalary)}
            </p>
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-500 font-medium">Basic  Salary</p>
            <p className="text-lg font-bold text-slate-800">
              {handleFormatAmount(basicSalary)}
            </p>
          </div>
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
            <p className="text-xs text-emerald-600 font-medium">
              Total Earnings
            </p>
            <p className="text-lg font-bold text-emerald-700">
              {handleFormatAmount(totalAllowances)}
            </p>
          </div>
          <div className="bg-rose-50 p-4 rounded-xl border border-rose-200">
            <p className="text-xs text-rose-600 font-medium">
              Total Deductions
            </p>
            <p className="text-lg font-bold text-rose-700">
              {handleFormatAmount(totalDeductionsOverview)}
            </p>
          </div>
          <div className="bg-primary/5 p-4 rounded-xl border border-primary/20 col-span-2 sm:col-span-1">
            <p className="text-xs text-primary/70 font-medium">Net Pay</p>
            <p className="text-lg font-bold text-primary">
              {handleFormatAmount(netPay)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {renderDetailTable(allowancesRows, "allowance")}
          {renderDetailTable(deductionsRows, "deduction")}
        </div>

        {salaryBreakdown.length > 0 && (
          <div className="pt-2">
            <SalaryBreakdownSummaryTable items={salaryBreakdown} />
          </div>
        )}

        <CommonModal
          title="Document Preview"
          show={showPreview}
          onClose={handleClosePreview}
          className="sm:max-w-3xl"
        >
          <DocumentPreview selectedDocument={selectedDoc} />
        </CommonModal>
      </div>
    );
  }

  // Create/Edit form
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {renderSalaryTable(earnings, "earning")}
        {renderSalaryTable(deductions, "deduction")}
      </div>

      {renderSalarySummary("Calculated Net Salary")}

      <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-slate-200">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (salaryCreated) {
              setShowForm(false);
              fetchData(true);
            } else {
              setShowForm(false);
            }
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isSubmitting}
          onClick={handleSubmit}
          className="gap-2"
        >
          {isSubmitting ? (
            <Spinner className="w-4 h-4" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {isSubmitting ? "Saving..." : "Save Salary"}
        </Button>
      </div>

      <CommonModal
        title="Document Preview"
        show={showPreview}
        onClose={handleClosePreview}
        className="sm:max-w-3xl"
      >
        <DocumentPreview selectedDocument={selectedDoc} />
      </CommonModal>
    </div>
  );
};

export default OnboardSalaryDetail;
